/**
 * ConditionEvaluator
 * 
 * Validates and evaluates condition strings against game state.
 * Uses strict regex validation to ensure safety and prevent malformed conditions.
 * 
 * Supported condition formats:
 * - flag:flagName - Check if flag is true
 * - !flag:flagName - Check if flag is false
 * - var:varName>value - Numeric comparison (>, <, >=, <=, ==, !=)
 */

import { GameState } from '../types';

// Strict regex patterns for condition validation
const FLAG_CONDITION_REGEX = /^(!)?flag:([a-zA-Z_][a-zA-Z0-9_]*)$/;
const VAR_CONDITION_REGEX = /^var:([a-zA-Z_][a-zA-Z0-9_]*)(>|<|>=|<=|==|!=)(\d+)$/;
const VISITED_CONDITION_REGEX = /^(!)?visited:([a-zA-Z_][a-zA-Z0-9_]*)$/;

export interface ConditionValidationError {
  isValid: false;
  error: string;
}

export interface ConditionValidationSuccess {
  isValid: true;
}

export type ConditionValidationResult = ConditionValidationError | ConditionValidationSuccess;

/**
 * Validates a condition string against strict regex patterns
 */
export function validateCondition(condition: string): ConditionValidationResult {
  if (!condition || typeof condition !== 'string') {
    return {
      isValid: false,
      error: 'Condition must be a non-empty string'
    };
  }

  const trimmed = condition.trim();

  if (trimmed.length === 0) {
    return {
      isValid: false,
      error: 'Condition cannot be empty or whitespace-only'
    };
  }

  // Check if it matches flag condition pattern
  if (trimmed.startsWith('flag:') || trimmed.startsWith('!flag:')) {
    if (!FLAG_CONDITION_REGEX.test(trimmed)) {
      return {
        isValid: false,
        error: `Invalid flag condition format: "${condition}". Expected format: "flag:flagName" or "!flag:flagName"`
      };
    }
    return { isValid: true };
  }

  // Check if it matches variable condition pattern
  if (trimmed.startsWith('var:')) {
    if (!VAR_CONDITION_REGEX.test(trimmed)) {
      return {
        isValid: false,
        error: `Invalid variable condition format: "${condition}". Expected format: "var:varName<operator><number>" (e.g., "var:trust>5")`
      };
    }
    return { isValid: true };
  }

  // Check if it matches visited condition pattern
  if (trimmed.startsWith('visited:') || trimmed.startsWith('!visited:')) {
    if (!VISITED_CONDITION_REGEX.test(trimmed)) {
      return {
        isValid: false,
        error: `Invalid visited condition format: "${condition}". Expected format: "visited:sceneId" or "!visited:sceneId"`
      };
    }
    return { isValid: true };
  }

  return {
    isValid: false,
    error: `Unknown condition type: "${condition}". Conditions must start with "flag:", "!flag:", "var:", "visited:", or "!visited:"`
  };
}

/**
 * Evaluates a flag condition against game state
 */
function evaluateFlagCondition(condition: string, gameState: GameState): boolean {
  const match = condition.match(FLAG_CONDITION_REGEX);
  
  if (!match || !match[2]) {
    throw new Error(`Invalid flag condition: ${condition}`);
  }

  const negation = match[1];
  const flagName = match[2];
  const flagValue = gameState.flags[flagName] ?? false;

  return negation ? !flagValue : flagValue;
}

/**
 * Evaluates a variable condition against game state
 */
function evaluateVarCondition(condition: string, gameState: GameState): boolean {
  const match = condition.match(VAR_CONDITION_REGEX);
  
  if (!match || !match[1] || !match[2] || !match[3]) {
    throw new Error(`Invalid variable condition: ${condition}`);
  }

  const varName = match[1];
  const operator = match[2];
  const valueStr = match[3];
  const varValue = gameState.variables[varName] ?? 0;
  const compareValue = parseInt(valueStr, 10);

  switch (operator) {
    case '>':
      return varValue > compareValue;
    case '<':
      return varValue < compareValue;
    case '>=':
      return varValue >= compareValue;
    case '<=':
      return varValue <= compareValue;
    case '==':
      return varValue === compareValue;
    case '!=':
      return varValue !== compareValue;
    default:
      throw new Error(`Unknown operator: ${operator}`);
  }
}

/**
 * Evaluates a visited condition against game state
 */
function evaluateVisitedCondition(condition: string, gameState: GameState): boolean {
  const match = condition.match(VISITED_CONDITION_REGEX);
  
  if (!match || !match[2]) {
    throw new Error(`Invalid visited condition: ${condition}`);
  }

  const negation = match[1];
  const sceneId = match[2];
  const visited = gameState.visitedScenes.has(sceneId);

  return negation ? !visited : visited;
}

/**
 * Evaluates a condition string against the current game state
 * 
 * @param condition - The condition string to evaluate
 * @param gameState - The current game state
 * @returns true if the condition is met, false otherwise
 * @throws Error if the condition is invalid (should be validated at load time)
 */
export function evaluateCondition(condition: string, gameState: GameState): boolean {
  if (!condition || typeof condition !== 'string') {
    throw new Error('Condition must be a non-empty string');
  }

  const trimmed = condition.trim();

  if (trimmed.includes(',')) {
    return trimmed.split(',').every(sub => evaluateCondition(sub.trim(), gameState));
  }

  // Evaluate flag conditions
  if (trimmed.startsWith('flag:') || trimmed.startsWith('!flag:')) {
    return evaluateFlagCondition(trimmed, gameState);
  }

  // Evaluate variable conditions
  if (trimmed.startsWith('var:')) {
    return evaluateVarCondition(trimmed, gameState);
  }

  // Evaluate visited conditions
  if (trimmed.startsWith('visited:') || trimmed.startsWith('!visited:')) {
    return evaluateVisitedCondition(trimmed, gameState);
  }

  throw new Error(`Invalid condition: ${condition}`);
}

/**
 * Validates multiple conditions (used during scenario validation)
 */
export function validateConditions(conditions: string[]): ConditionValidationResult {
  for (const condition of conditions) {
    const result = validateCondition(condition);
    if (!result.isValid) {
      return result;
    }
  }
  return { isValid: true };
}

/**
 * ConditionEvaluator class for convenience
 */
export class ConditionEvaluator {
  /**
   * Validates a condition string
   */
  static validate(condition: string): ConditionValidationResult {
    return validateCondition(condition);
  }

  /**
   * Validates multiple conditions
   */
  static validateAll(conditions: string[]): ConditionValidationResult {
    return validateConditions(conditions);
  }

  /**
   * Evaluates a condition against game state
   */
  static evaluate(condition: string, gameState: GameState): boolean {
    return evaluateCondition(condition, gameState);
  }
}

